import * as _ from "lodash"
import { BehaviorSubject } from "rxjs"
import { IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions } from "../PA.Core/PA.Core.IF.BO"
import { IwbMetric, IwbPlanOverride } from "../PA.Core/PAdbSchemaRowsIF"
import { IHaveProductGroups, IHaveSecurity } from "../PA.Core/PA.Core.IF.Aspects"
import { IDateRange, IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic"
import { IdentityProvider } from "../PA.Core/Basic/Identity"
import { ASync } from "../PA.Core/Basic/ASync"
import { MessageProvider } from "../PA.Core/Basic/Messages"
import { ProgressProvider } from "../PA.Core/Basic/Progress"

import { PAdbContext } from "../PA.Core/PAdbContext"
import {
    esWB_Baseline_Metrics,
    esWB_Hierarchy,
    esWB_Metric,
    esWB_Period,
    esWB_Period_Type,
    esWB_Plan,
    esWB_Plan_Funding_Metrics,
    esWB_Plan_Override,
    esWB_Plan_Product,
    esWB_Plan_Product_Group,
    esWB_Plan_Product_Metrics,
    esWB_Plan_Redistrib_Volume_Metrics,
    esWB_Plan_Type,
    esWB_Plan_Volume_Metrics,
    esWB_Product_Group, esWB_Product_Type
} from "../PA.Core/PAdbSchema"

import {
    WB_BaselineMetricsRow, WB_CubeSourcePerspectiveRow, WB_HierarchyRow,
    WB_MetricRow,
    WB_PeriodRow,
    WB_PlanOverrideRow,
    WB_PlanProductGroupRow,
    WB_PlanProductMetricsRow,
    WB_PlanRow,
    WB_PlanVolumeMetricsRow,
    WB_ProductGroupRow, WB_ProductTypeRow
} from "../PA.Core/PAdbSchemaRows"

import { ESRecord } from "../PA.Core/lib/ESRecord"
import { ESDataStore } from "../PA.Core/lib/ESDataStore"
import { PAusrContext } from "../PA.Core/PAusrContext"
import { PAEnvironment } from "../PA.Core/PAEnvironment"
import { Subscription } from "rxjs/Subscription"
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver"

import {
    IHaveDisplays,
    IHaveDisplaysChangedEvent,
    IHavePlanCalculator,
    IHavePlanMetrics,
    IHavePlanMetricsChangedEvent,
    IHavePlanStandardCalculator,
    IPlanCalculator,
    IPlanStandardCalculator,
    ITPMPlan,
    ITPMPlanChangedEvent
} from "../PA.TPM.Core/PA.TPM.IF.TPM.Plan.Aspects"
import { IHavePlannedPeriods } from "../PA.TPM.Core/PA.TPM.IF.Planning.Aspects"
import { ExecutionSerializer } from "../PA.Core/Basic/ExecutionSerializer"
import { PeriodService } from "../PA.MD.Services/PeriodService"
import { TPMPlanCalculator } from "../PA.TPM.Services/TPMPlanCalculator"
import { TPMPlanStandardCalculator } from "../PA.TPM.Services/TPMPlanStandardCalculator"
import { IPolicyCollection } from "../PA.Core/PA.Core.IF.Policy"
import { PolicyCollection } from "../PA.Core/Security/PolicyCollection"
import { PolicyBOis } from "../PA.Core/Security/PolicyBOis"
import { PolicyHasRole } from "../PA.Core/Security/PolicyHasRole"
import { IHaveEditorLock, ILock } from "../PA.Concurrency.Core/PA.Concurrency.IF.Lock"
import { PALockClient } from "../PA.Concurrency.Services/PALockClient"
import { IHaveRefCounter, IRefCounter } from "../PA.Concurrency.Core/PA.Concurrency.IF.RefCounter"
import { RefCounter } from "../PA.Concurrency.Core/RefCounter"
import { DefaultChanges } from "../PA.Core/BusinessObject/BusinessObjectChanges"
import { CubeSource } from "../PA.HyperRectangle.Core/CubeSource"
import { DimensionType } from "../PA.Core/PA.Core.IF.Hierarchy"
import { ICubePerspectiveOptions, ICubeSource, IMetricView } from "../PA.HyperRectangle.Core/PA.HyperRectangle.IF"
import { DataSourceJSData } from "../PA.HyperRectangle.Core/DataSourceJSData"
import { AuditService } from "../PA.Core/Security/AuditService"
import { TPMPlanBOAudit } from "./TPMPlanBO.Audit"
import { TPMPlanBOValidations } from "./TPMPlanBO.validations"
import { IHRTableModelOptions } from "../../client-ui5/PA-Controls/HyperRectangleTableModels"
import { DimensionPlacement } from "../PA.Reporting.Core/PA.Reporting.IF"
import { DateUtils } from "../PA.Core/Basic/Dates"
import { IHierarchyStructure, IHierarchyStructureNavigator, INode } from "../PA.MD.Core/PA.MD.IF.Hierarchy"
import { HierarchyStructureClient } from '../PA.MD.Hierarchy/HierarchyStructureClient'


//<editor-fold desc = "Plan BO Options Interface and Defaults ">

export interface ITPMPlanCreateOptions {
    bEagerLoadAspectInterfaces?: boolean;
    dbContext?: PAdbContext;
}

const TPMPlanCreateOptions_default: ITPMPlanCreateOptions = {
    bEagerLoadAspectInterfaces: false
}

export interface ITPMPlanLoadOptions {
    dbContext?: PAdbContext;
}

const TPMPlanLoadOptions_default: ITPMPlanLoadOptions = {}

export interface ITPMPlanSaveValidateOptions {
    Validate?: {
        bSkipAllValidations?: boolean;
    };
    Save?: {};
}

const TPMPlanSaveValidateOptions_default: ITPMPlanSaveValidateOptions = {
    Validate: {
        bSkipAllValidations: false
    },
    Save: {}
}

export interface ITPMPlanDeleteOptions {
}

export interface ITPMPlanGetBaselineData {
    [wb_baseline_id: number]: {
        dctPrdGrpId: {
            [id: number]: {
                dctPeriodId: { [id: number]: number };
            };
        };
    };
}

const TPMPlanDeleteOptions_default: ITPMPlanDeleteOptions = {}

const PlanMetrics_ProductGroupLoadAllThreshold = 20

//</editor-fold desc>

export class TPMPlanBO
    implements IBusinessObject,
        ITPMPlan,
        IHaveProductGroups,
        IHavePlannedPeriods,
        IHavePlanMetrics,
        IHavePlanCalculator,
        IHavePlanStandardCalculator,
        IHaveSecurity,
        IHaveDisplays,
        IHaveEditorLock,
        IHaveRefCounter {
    // IRecordResolverProvider  <static>
    //<editor-fold desc = "Plan Attributes and Constructor">
    CreateOptions: ITPMPlanCreateOptions
    Identity: IdentityProvider

    LoadOptions: ITPMPlanLoadOptions
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    _bIsLoadedInternal: boolean
    _OnLoadCompleteInternal: Promise<TPMPlanBO>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_Plan: WB_PlanRow

    dctBaselineAcceptedAuditService: AuditService[]
    _BaselineAcceptedChanged: BehaviorSubject<IBusinessObject>


    _wb_entity_id_target: number

    usrContext: PAusrContext

    constructor(options?: ITPMPlanCreateOptions) {
        this.CreateOptions = options || {}
        _.defaults(this.CreateOptions, TPMPlanCreateOptions_default)

        if ("dbContext" in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext
        }

        this.usrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        this.Identity = new IdentityProvider({ id: 0, name: "Undefined Plan" })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._bIsLoadedInternal = false

        this._OnLoadComplete = undefined
        this._OnLoadCompleteInternal = undefined
        this.LoadProgressProvider = new ProgressProvider("Loading", this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_Plan = undefined

        this.dctBaselineAcceptedAuditService = []
        this._BaselineAcceptedChanged = new BehaviorSubject<IBusinessObject>(this)

        this._wb_entity_id_target = 0

        this.ITPMPlan_init()
        this.IHaveProductGroups_init()
        this.IHavePlanVolumeMetrics_init()
        this.ICubeSourcePlanMetrics_init()
        this.IHaveDisplays_init()
        this.IHavePlanCalculator_init()
        this.IHavePlanStandardCalculator_init()
        this.IHaveSecurity_init()
        this.RefCounter_Init()
    }

    //</editor-fold>

    get WB_Plan(): WB_PlanRow {
        return this.drWB_Plan
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this

        //precheck promises.. to see if already loading..?

        self.LoadProgressProvider.start("Creating TPM Plan")

        self._OnLoadCompleteInternal = new Promise<TPMPlanBO>((resolve, reject) => {
            if (self.bIsLoaded) {
                // "Plan already loaded.";
                resolve(self)
                return
            }

            self.LoadOptions = options || {}
            _.defaults(self.LoadOptions, TPMPlanLoadOptions_default)

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({})
            }

            if (_.isEmpty(self.dbContext)) {
                reject("Unable to determine dbContext for new TPM Plan.")
                return
            }

            reject("TPM PlanBO creation is Not Implemented")
        })

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self._OnLoadCompleteInternal
                .then((aBO) => {
                    if (self.bIsLoaded) {
                        resolve(self)
                        return
                    }

                    // Run after Load
                    self._bIsLoadedInternal = true
                    self._OnLoadCompleteInternal = undefined
                    self._AfterBoLoadCompleteInternal()
                        .then(() => {
                            self._bIsLoaded = true
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.finish()
                            if (self.CreateOptions.bEagerLoadAspectInterfaces) {
                                self.OnIHaveProductGroupsLoaded
                            }
                            return self.OnICubeSourcePlanMetricsLoaded
                        })
                        .then(() => {
                            resolve(self)
                        })
                        .catch((err) => {
                            self._OnLoadCompleteInternal = undefined
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.exception(err)
                            resolve(err)
                        })
                })
                .catch((err) => {
                    self._OnLoadCompleteInternal = undefined
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(err)
                    resolve(self)
                })
        })

        return this._OnLoadComplete
    }

    Load(anId: number | number, options: object): Promise<IBusinessObject> {
        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")")

        self._OnLoadCompleteInternal = new Promise<TPMPlanBO>((resolve, error) => {
            if (self.bIsLoaded) {
                resolve(self) // "Plan already loaded.";
                return
            }

            self.LoadOptions = options || {}
            _.defaults(self.LoadOptions, TPMPlanLoadOptions_default)

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({})
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for new Plan.")
                return
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = TPMPlanBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = TPMPlanBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function() {
                    let aPlanRow: any = recordResolver.get(esWB_Plan, anId)

                    if (_.isEmpty(aPlanRow)) {
                        console.log("Plan Record Resolver unable to locate Plan for id: ", anId)
                        error("Plan not Found")
                        return
                    }

                    self.drWB_Plan = aPlanRow
                    self.Identity.setAll(
                        self.WB_Plan.cif.WB_PLAN_ID,
                        self.WB_Plan.cif.PLAN_ID,
                        self.WB_Plan.cif.PLAN_NAME
                    )
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    )
                    self.Identity.listenToRecordUpdates(self.WB_Plan, "WB_PLAN_ID", "PLAN_ID", "PLAN_NAME")
                    self.Identity.enableRecordWriteBack(self.WB_Plan, undefined, "PLAN_NAME")
                    self.Identity.setSalesOrg(self.WB_Plan.cif.WB_SALESORG_ID)

                    resolve(self)
                })
                .catch(function(err: any) {
                    error(err)
                    return
                })
        })

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self._OnLoadCompleteInternal
                .then((aBO) => {
                    if (self.bIsLoaded) {
                        resolve(self)
                        return
                    }

                    // Run after Load
                    self._bIsLoadedInternal = true
                    self._OnLoadCompleteInternal = undefined
                    self._AfterBoLoadCompleteInternal()
                        .then(() => {
                            self._bIsLoaded = true
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.finish()

                            if (self.CreateOptions.bEagerLoadAspectInterfaces) {
                                self.OnIHaveProductGroupsLoaded
                            }
                            return self.OnICubeSourcePlanMetricsLoaded
                        })
                        .then(() => {
                            resolve(self)
                        })
                        .catch((err) => {
                            debugger;
                            self._OnLoadCompleteInternal = undefined
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.exception(err)
                            resolve(err)
                        })
                })
                .catch((err) => {
                    debugger;
                    self._OnLoadCompleteInternal = undefined
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(err)
                    resolve(self)
                })
        })

        return this._OnLoadComplete
    }

    get WB_ENTITY_ID_TARGET(): number {
        return this._wb_entity_id_target
    }

    Reload(opts?: any): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            if (!self.bIsLoaded) {
                error("Reload: TPM PlanBO not loaded")
                return
            }

            let id = self.WB_Plan.cif.WB_PLAN_ID

            self.dbContext
                .purgeCache(esWB_Plan, self.WB_Plan, {})
                .then((ignore) => {
                    self._bIsLoaded = false
                    self.IHavePlanVolumeMetrics_beforeReload()
                    self.ICubeSourcePlanMetrics_beforeReload()
                    self.IHaveDisplays_beforeReload()
                    self.IHaveProductGroup_BeforeReload()
                    self._bIHavePlannedPeriodsLoaded = false
                    self._OnIHavePlannedPeriodsLoaded = undefined
                    self._OnLoadComplete = undefined
                    self._OnLoadCompleteInternal = undefined
                    self.IHaveSecurity_beforeReload()

                    return self.Load(id, { dbContext: self.dbContext })
                })
                .then((aBO) => {
                    if (!aBO.bIsLoaded) {
                        console.log("Unable to Load BO in Reload function")
                    }
                    // Should we fire anyexisting Behavior Subjects?
                    self.ProductGroupsChanged.next(self)
                    self.PlanVolumeMetricsCollectionChanged.next(self)
                    self.PlanDisplaysCollectionChanged.next(self)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            let tasks: Promise<any>[] = []
            self.LoadProgressProvider.setProgressMessage("PlanBO.PostLoad", "Processing Loaded Data")
            tasks.push(self.LoadProductHierarchy())
            tasks.push(self.LoadPlanLevelEntityId())
            tasks.push(self._Overrides_OnBoLoadComplete())
            tasks.push(self.ITPMPlan_OnLoadComplete())
            tasks.push(self.IHavePlanVolumeMetrics_OnLoadComplete())
            tasks.push(self.ICubeSourcePlanMetrics_OnLoadComplete())
            tasks.push(self.IHaveDisplays_OnLoadComplete())
            tasks.push(self.IHavePlanStandardCalculator_OnLoadComplete())
            Promise.all(tasks)
                .then((results: any) => {
                    return self.IHaveSecurity_OnLoadComplete()
                })
                .then(() => {
                    if (!this.RefCounter.RefCount) self.RefCounter.acquireReference()
                    resolve()
                })
                .catch((err: any) => {
                    error(err)
                })
        })
    }

    async LoadProductHierarchy() {
        const self = this
        if (_.isEmpty(self.WB_Plan.HierarchyRow)) {
            let hierarchyRows = await self.dbContext.findAll(esWB_Hierarchy, {
                where: {
                    TARGET_WB_ID: self.WB_Plan.WB_PlanTypeRow.cif.WB_PRDH_ID,
                    DELETED_IND: { neq: "1" }
                }
            })
            self.WB_Plan.HierarchyRow = _.first(hierarchyRows)
        }
    }

    async LoadPlanLevelEntityId() {
        const self = this
        if (self._wb_entity_id_target > 0) return
        let ppgs = _.filter(self.WB_Plan.WB_Plan_ProductGroupRows, r => r.isActive)
        if (ppgs.length > 0) {
            let grpType: WB_ProductTypeRow = <WB_ProductTypeRow>await self.dbContext.find(esWB_Product_Type, _.first(ppgs).WB_ProductGroupRow.cif.WB_PRDM_ID)
            self._wb_entity_id_target = grpType?.cif?.WB_ENTITY_ID

        }
    }

    _Overrides_OnBoLoadComplete(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            var drOverrides: WB_PlanOverrideRow
            if (_.isEmpty(self.drWB_Plan.WB_PlanOverrideRow)) {
                drOverrides = <any>self.dbContext.createRecord(esWB_Plan_Override)
                drOverrides.setDefaults()
                var newRow: IwbPlanOverride = drOverrides
                newRow.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                self.drWB_Plan.WB_PlanOverrideRow = drOverrides
                self.dbContext.add(esWB_Plan_Override, drOverrides)
            } else drOverrides = <WB_PlanOverrideRow>self.drWB_Plan.WB_PlanOverrideRow

            // Create Metrics row if empty
            if (_.isEmpty(drOverrides.WB_MetricRow_Actual)) {
                self.dbContext.createRecordWithKey(esWB_Metric).then((drMetrics: WB_MetricRow) => {
                    drMetrics.setDefaults()
                    var Metrics: IwbMetric = drMetrics
                    Metrics.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                    Metrics.REFERENCE_LEVEL_ID = "Plan"
                    Metrics.METRIC_TYPE_ID = "Actual"
                    Metrics.REFERENCE_ID = self.WB_Plan.cif.WB_PLAN_ID
                    Metrics.REFERENCE_ID_UI = self.WB_Plan.cif.PLAN_ID
                    Metrics.WB_PRDGRP_ID = 0
                    drOverrides.WB_MetricRow_Actual = drMetrics
                    self.dbContext.add(esWB_Metric, drMetrics)
                    resolve()
                })
            } else {
                resolve()
            }
        })
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    Validate(options: ITPMPlanSaveValidateOptions): Promise<IBusinessObject> {
        const self = this

        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start(
            "Validating " + self.WB_Plan.cif.PLAN_NAME + "(" + self.WB_Plan.cif.PLAN_ID + ")"
        )

        return new Promise<IBusinessObject>((resolve, reject) => {
            options = options || {}
            _.defaultsDeep(options, TPMPlanSaveValidateOptions_default)

            self.ValidateOptions = options

            if (_.isEmpty(self.dbContext)) {
                var sError = "Unable to determine dbContext to Validate Plan."
                self.ValidateProgressProvider.exception(sError)
                reject(sError)
                return
            }

            var subProgressChanged: Subscription = undefined

            ASync.resolve()
                .then((ignore) => {
                    // Do Plan Level Validations..

                    return ASync.resolve(self)
                })
                .then((ignore) => {
                    if (subProgressChanged) {
                        subProgressChanged.unsubscribe()
                    }

                    return ASync.resolve()
                })
                .then((ignore) => {
                    self._bIsValidated = true
                    self.ValidateProgressProvider.finish()
                    resolve(self)
                })
                .catch((err) => {
                    self._bIsValidated = false
                    self.ValidateProgressProvider.exception(err)
                    resolve(self)
                })
        })
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: ITPMPlanSaveValidateOptions): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: ITPMPlanSaveValidateOptions): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: ITPMPlanSaveValidateOptions): Promise<IBusinessObject> {
        const self = this
        let bQuit = false

        this.SaveProgressProvider.start("Saving " + this.WB_Plan.cif.PLAN_NAME + "(" + this.WB_Plan.cif.PLAN_ID + ")")

        return new Promise<IBusinessObject>((resolve, reject) => {
            if (self.EditorLock.Status === "ReadOnly") {
                var sError = "Unable to Save Locked Plan."
                self.SaveProgressProvider.exception(sError)
                reject(sError)
                return
            }

            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false
                var sError = "Unable to determine dbContext to Save Plan."
                self.SaveProgressProvider.exception(sError)
                reject(sError)
                return
            }

            options = options || {}
            _.defaultsDeep(options, TPMPlanSaveValidateOptions_default)
            self.SaveOptions

            var saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            ASync.resolve()
                .then((ignored) => {
                    self.SaveProgressProvider.setPercentComplete(0.1)
                    if (options.Validate.bSkipAllValidations) {
                        return ASync.resolve()
                    } else {
                        return self.Validate(options)
                        // Run Validate
                    }
                })
                .then((ignore) => {
                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        bQuit = true
                        return
                    }
                    self.drWB_Plan.cif.MODIFIED_DTTS = DateUtils.convertToSAPBWTs(new Date())
                    return saveFunction(esWB_Plan, self.drWB_Plan)
                })
                .then((ignore) => {
                    if (bQuit) {
                        return
                    }
                    self.SaveProgressProvider.setPercentComplete(0.5)

                    var tasks: Promise<any>[] = []

                    //tasks.push(saveFunction(esWB_Plan, self.drWB_Plan))
                    tasks.push(saveFunction(esWB_Plan_Override, self.drWB_Plan.WB_PlanOverrideRow))

                    if (self.drWB_Plan.WB_PlanOverrideRow) {
                        tasks.push(saveFunction(esWB_Metric, self.drWB_Plan.WB_PlanOverrideRow.WB_MetricRow_Actual))
                    }

                    tasks.push(saveFunction(esWB_Plan_Product_Group, self.drWB_Plan.WB_Plan_ProductGroupRows))
                    tasks.push(saveFunction(esWB_Plan_Volume_Metrics, self.drWB_Plan.WB_PlanVolumeMetricsRows))
                    tasks.push(saveFunction(esWB_Plan_Funding_Metrics, self.drWB_Plan.WB_PlanFundingMetricsRows))

                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    self.SaveProgressProvider.setPercentComplete(0.8)
                    var auditTasks: Promise<void>[] = []
                    if (self.dctBaselineAcceptedAuditService.length > 0) {
                        _.forEach(self.dctBaselineAcceptedAuditService, (anAuditService: AuditService) => {
                            auditTasks.push(anAuditService.Save(bBatch))
                        })
                    }

                    return Promise.all(auditTasks)
                })
                .then((ignored) => {
                    self.BaselineAcceptedChanged.next(self)
                    if (bQuit) {
                        return
                    }

                    // Validate all event
                    return ASync.resolve()
                })
                .then((ignored) => {
                    if (bQuit) {
                        return
                    }
                    self.SaveProgressProvider.setPercentComplete(1)

                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true
                        })
                    } else {
                        self._bIsSaved = true
                    }

                    setTimeout(function() {
                        self.SaveProgressProvider.finish()
                    }, 4000)
                })
                .then((ignore) => {
                    if (self.EditorLock)
                        self.EditorLock.touch().finally(() => {
                        })
                    resolve(self)
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err)
                    resolve(self)
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    Delete(): Promise<IBusinessObject> {
        const self = this
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start(
            "Deleting " + self.WB_Plan.cif.PLAN_NAME + "(" + self.WB_Plan.cif.PLAN_ID + ")"
        )
        return new Promise<IBusinessObject>((resolve, reject) => {
            TPMPlanBOValidations.ValidateBeforeDelete(self)
                .then((ignore) => {
                    if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                        return
                    }
                    self.drWB_Plan.markDeleted()
                    _.forEach(self.drWB_Plan.WB_Plan_ProductGroupRows, (a) => a.markDeleted())
                    _.forEach(self.drWB_Plan.WB_PlanVolumeMetricsRows, (a) => a.markDeleted())
                    _.forEach(self.drWB_Plan.WB_PlanFundingMetricsRows, (a) => a.markDeleted())
                    self.DeleteProgressProvider.finish()
                    return self.SaveUsing(false, {})
                })
                .then((ignore) => {
                    if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                        return
                    }
                    self._bIsDeleted = true
                })
                .then((ignore) => {
                    self.DeleteProgressProvider.finish()
                    resolve(self)
                })
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        opts.dctExcludeFields = opts.dctExcludeFields || {}
        opts.bIgnoreStandardFields = true
        opts.dctIgnoreChanges = opts.dctIgnoreChanges || {}
        opts.dctIgnoreChanges[esWB_Plan_Volume_Metrics] = { bNew: true, bDeletes: true }
        opts.dctExcludeFields[esWB_Plan] = [
            "METRICVIEWSELECTED",
            "PERIODMETRICVIEWSELECTED",
            "METRIC_PERSPECTIVE_OPTIONS",
            "METRIC_TABLE_OPTIONS",
            "SVF_PERSPECTIVE_OPTIONS",
            "SVF_TABLE_OPTIONS",
            "BVP_PERSPECTIVE_OPTIONS",
            "BVP_TABLE_OPTIONS",
            "FC_OPTIONS"
        ]

        return DefaultChanges(this, this.drWB_Plan, opts)
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveProductGroup operations">

    _bIHaveProductGroupsLoaded: boolean
    _OnIHaveProductGroupsLoaded: Promise<IHaveProductGroups>
    _ProductGroupsAll: WB_ProductGroupRow[]

    IHaveProductGroups_init() {
        this.ProductGroupsChanged = new BehaviorSubject<IHaveProductGroups>(this)
        this._bIHaveProductGroupsLoaded = false
        this._OnIHaveProductGroupsLoaded = undefined
        this._ProductGroupsAll = []
    }

    private IHaveProductGroup_BeforeReload(): void {
        this._bIHaveProductGroupsLoaded = false
        this.dbContext.purgeCache(esWB_Plan_Product_Group, this.WB_Plan.WB_Plan_ProductGroupRows, {})
        this._OnIHaveProductGroupsLoaded = undefined
    }

    ProductGroupsChanged: BehaviorSubject<IHaveProductGroups>

    get bIHaveProductGroupsLoaded(): boolean {
        return this._bIHaveProductGroupsLoaded
    }

    get OnIHaveProductGroupsLoaded(): Promise<IHaveProductGroups> {
        const self = this
        let hsn: IHierarchyStructureNavigator = undefined
        let hs: IHierarchyStructure = undefined
        if (self.bIHaveProductGroupsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveProductGroupsLoaded))
            return self._OnIHaveProductGroupsLoaded // Load already started, wait on same Promise

        let rv = (async () => {
            // If the BO is not loaded, throw an error.. It should have been started
            if (!self.bIsLoaded && _.isEmpty(self.OnLoadComplete)) {
                throw "Plan isn't Loaded or Loading.. Can't determine Product Groups"
            }

            if (!self.bIsLoaded) {
                await self.OnLoadComplete

                if (!self.bIsLoaded)
                    throw 'Waited on Plan Load to complete but still isn\'t Loaded'
            }

            let hsn = await HierarchyStructureClient.getHierarchyNavigatorPeriod(
                self.WB_Plan.HierarchyRow.cif.WB_HIERARCHY_ID,
                self.WB_Plan.cif.WB_PERIOD_ID
            )
            let hs = hsn.hierarchyStructure
            let prntNode = hs.Nodes.findNodeByWbIdSync(esWB_Product_Group, self.WB_Plan.cif.WB_PRDGRP_ID_PLANLEVEL)
            let leafNodes = hsn.getChildNodesOfLevel(prntNode, self.WB_Plan.WB_PlanTypeRow.cif.PRDGRP_LEVEL_ID)

            self._ProductGroupsAll = <WB_ProductGroupRow[]>await self.dbContext.findUsingCache(esWB_Product_Group, _.map(leafNodes, ln => ln.wbId))

            // Setup Product Group Collection, flag loaded.
            this._bIHaveProductGroupsLoaded = true
            this._OnIHaveProductGroupsLoaded = undefined

            return self
        })();

        self._OnIHaveProductGroupsLoaded = rv
        return rv
    }

    get ProductGroups(): WB_ProductGroupRow[] {
        return _.map(
            _.filter(this.WB_Plan.WB_Plan_ProductGroupRows, (aRow) => aRow.isActive),
            (aRow) => aRow.WB_ProductGroupRow
        )
    }

    get AllProductGroups(): WB_ProductGroupRow[] {
        return this._ProductGroupsAll
    }

    get ProductGroupsReadOnly(): boolean {
        // TODO: Check Security Roles..
        return false
    }

    addProductGroups(arrProductGroupId: number[]): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.dbContext
                .findUsingCache(esWB_Product_Group, arrProductGroupId)
                .then((arrPrdGrpRows: WB_ProductGroupRow[]) => {
                    async function createProductGroupRows() {
                        for (let idx in arrPrdGrpRows) {
                            let aWB_PrdGrpRow = arrPrdGrpRows[idx]
                            let newRecord: WB_PlanProductGroupRow = <any>(
                                await self.dbContext.createRecordWithKey(esWB_Plan_Product_Group)
                            )
                            newRecord.setDefaults()
                            newRecord.cif.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                            newRecord.cif.WB_PRDGRP_ID = aWB_PrdGrpRow.cif.WB_PRDGRP_ID
                            newRecord.WB_ProductGroupRow = aWB_PrdGrpRow
                            newRecord.cif.MANUAL_IND = "1"
                            self.dbContext.add(esWB_Plan_Product_Group, newRecord)
                            self.PlanVolumeMetrics_onPrdGrpAdd(aWB_PrdGrpRow)
                            self.CubeSource_onPrdGrpAdd(aWB_PrdGrpRow)
                        }

                        self.ProductGroupsChanged.next(self)
                    }

                    return createProductGroupRows()
                })
                .then((ignore) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    addProductGroup(ProductGroupId: number): Promise<void> {
        const self = this

        return self.addProductGroups([ProductGroupId])
    }

    removeProductGroups(arrProductGroupId: number[]): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            let bChanged = false
            _.forEach(self.WB_Plan.WB_Plan_ProductGroupRows, (aRow) => {
                if (aRow.isActive && _.includes(arrProductGroupId, aRow.cif.WB_PRDGRP_ID)) {
                    bChanged = true
                    aRow.markDeleted()
                }
            })

            if (bChanged) {
                self.ProductGroupsChanged.next(self)
            }

            resolve()
        })
    }

    removeProductGroup(ProductGroupId: number): Promise<void> {
        const self = this

        return self.removeProductGroups([ProductGroupId])
    }

    hasProductGroup(ProductGroupId: number): boolean {
        const self = this
        let returnValue = false

        _.forEach(self.WB_Plan.WB_Plan_ProductGroupRows, (aRow) => {
            if (aRow.isActive && aRow.cif.WB_PRDGRP_ID == ProductGroupId) {
                returnValue = true
                return false // stop iterating
            }
        })

        return returnValue
    }

    isProductGroupEditable(ProductGroupId: number): boolean {
        return true
    }

    //</editor-fold>

    //<editor-fold desc = "Baseline operations">

    async UpdateBaseline(pWB_Baseline_IDs: { key: string; wb_baseline_id: number }[]): Promise<void> {
        const self = this

        await self.OnIHaveProductGroupsLoaded
        await self.OnIHavePlannedPeriodsLoaded

        const arrWB_Baseline_IDs = _.map(pWB_Baseline_IDs, (aBL) => aBL.wb_baseline_id)
        const arrWB_PrdGrp_IDs = _.map(self.ProductGroups, (aPG) => aPG.cif.WB_PRDGRP_ID)
        const arrWB_Period_IDs = _.map(self.PlannedPeriods, (aPrd) => aPrd.cif.WB_PERIOD_ID)

        if (_.isEmpty(arrWB_PrdGrp_IDs) || _.isEmpty(arrWB_Period_IDs) || _.isEmpty(arrWB_Baseline_IDs)) {
            return
        }

        const arrBaselinesMetrics: WB_BaselineMetricsRow[] = await self.dbContext.findAll(esWB_Baseline_Metrics, {
            where: {
                WB_CUST_ID: self.WB_Plan.cif.WB_CUST_ID_PLANNING,
                WB_BASELINE_ID: { inq: arrWB_Baseline_IDs },
                WB_PRDGRP_ID: { inq: arrWB_PrdGrp_IDs },
                WB_PERIOD_ID: { inq: arrWB_Period_IDs },
                DELETED_IND: { neq: "1" }
            }
        })

        let dctBaselineData: ITPMPlanGetBaselineData = {}
        _.forEach(arrBaselinesMetrics, (aRow: WB_BaselineMetricsRow) => {
            let wb_baseline_id = aRow.cif.WB_BASELINE_ID
            let wb_prdgrp_id = aRow.cif.WB_PRDGRP_ID
            let wb_period_id = aRow.cif.WB_PERIOD_ID
            let baseln_qty = aRow.cif.BASELINE_QTY || 0

            if (!(wb_baseline_id in dctBaselineData)) {
                dctBaselineData[wb_baseline_id] = { dctPrdGrpId: {} }
            }

            if (!(wb_prdgrp_id in dctBaselineData[wb_baseline_id].dctPrdGrpId)) {
                dctBaselineData[wb_baseline_id].dctPrdGrpId[wb_prdgrp_id] = { dctPeriodId: {} }
            }

            dctBaselineData[wb_baseline_id].dctPrdGrpId[wb_prdgrp_id].dctPeriodId[wb_period_id] = baseln_qty
        })

        const dctBaselineKeys = _.keyBy(pWB_Baseline_IDs, (a) => a.key)
        const ds: DataSourceJSData = <DataSourceJSData>self._cubeSourcePlanMetrics.getDataSource("PlanVMT")
        const arrPlanVMTRows: WB_PlanVolumeMetricsRow[] = <WB_PlanVolumeMetricsRow[]>await ds.getRowScope({})

        for (const key in dctBaselineKeys) {
            const wb_baseline_id = dctBaselineKeys[key].wb_baseline_id

            if (wb_baseline_id in dctBaselineData) {
                const baselineData = dctBaselineData[wb_baseline_id]
                _.forEach(arrPlanVMTRows, (aRow) => {
                    let aValue = 0
                    let aPG = baselineData.dctPrdGrpId[aRow.cif.WB_PRDGRP_ID]
                    if (aPG) {
                        aValue = aPG.dctPeriodId[aRow.cif.WB_PERIOD_ID] || 0
                    }
                    // @ts-ignore
                    aRow[key] = aValue
                })
            }
        }

        self._cubeSourcePlanMetrics.OnCubeSourceMetricChange.next({
            Source: self._cubeSourcePlanMetrics,
            Metric: [
                "PlanVMT_CNS_BASELN_PY_QTY",
                "PlanVMT_CNS_BASELN_ALT_QTY",
                "PlanVMT_SHIP_BASELN_PY_QTY",
                "PlanVMT_SHIP_BASELN_ALT_QTY"
            ]
        })
    }

    //</editor-fold>

    //<editor-fold desc = "ITPMPlan operations">

    private _TPMPlanPropertyChanged: BehaviorSubject<ITPMPlanChangedEvent>

    private ITPMPlan_init() {
        this._TPMPlanPropertyChanged = new BehaviorSubject<ITPMPlanChangedEvent>(undefined)
    }

    private ITPMPlan_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.WB_Plan.setupOnChangeEvents([], self.ITPMPlan_onRecordChange.bind(self), self)
            resolve()
        })
    }

    private ITPMPlan_onRecordChange(record: WB_PlanRow, propertyName: string, propertyValue: any, options: TPMPlanBO) {
        this.TPMPlanPropertyChanged.next({
            TPMPlanBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    get TPMPlanPropertyChanged(): BehaviorSubject<ITPMPlanChangedEvent> {
        return this._TPMPlanPropertyChanged
    }

    //</editor-fold>

    //<editor-fold desc = "IHavePlannedPeriods operations">

    private _bIHavePlannedPeriodsLoaded: boolean
    private _PlannedPeriods: WB_PeriodRow[]
    private _OnIHavePlannedPeriodsLoaded: Promise<void>

    private IHavePlannedPeriods_init() {
        this._PlannedPeriods = []
        this._bIHavePlannedPeriodsLoaded = false
        this._OnIHavePlannedPeriodsLoaded = undefined
    }

    get bIHavePlannedPeriodsLoaded(): boolean {
        return this._bIHavePlannedPeriodsLoaded
    }

    get OnIHavePlannedPeriodsLoaded(): Promise<void> {
        const self = this

        if (self.bIHavePlannedPeriodsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isUndefined(self._OnIHavePlannedPeriodsLoaded)) return self._OnIHavePlannedPeriodsLoaded // Load already started, wait on same Promise

        let OnLoadPromise = new Promise<void>((resolve, error) => {
            // If the BO is not loaded, throw an error.. It should have been started
            if (!self.bIsLoaded && _.isEmpty(self.OnLoadComplete)) {
                error("Plan isn't Loaded or Loading.. Can't determine Planned Periods")
                return
            }

            let boLoadPromise = undefined
            if (self.bIsLoaded) {
                boLoadPromise = ASync.resolve(self)
            } else {
                boLoadPromise = self.OnLoadComplete
            }
            boLoadPromise
                .then((aPlanBO) => {
                    if (!aPlanBO.bIsLoaded) {
                        error("Waited on Plan Load to complete but still isn't Loaded")
                        return
                    }

                    // Pull the List of Available ProductGroups
                    return PeriodService.getAllChildrenByWBIDChildTypeWbId(
                        self.WB_Plan.WB_PlanTypeRow.cif.WB_PERIODM_ID,
                        self.WB_Plan.cif.WB_PERIOD_ID
                    )
                })
                .then((arrPeriodsCached) => {
                    return self.dbContext.findUsingCache(
                        esWB_Period,
                        _.map(arrPeriodsCached, (a) => a.cif.WB_PERIOD_ID) || []
                    )
                })
                .then((arrPeriods: WB_PeriodRow[]) => {
                    self._PlannedPeriods = arrPeriods

                    this._bIHavePlannedPeriodsLoaded = true
                    this._OnIHavePlannedPeriodsLoaded = undefined

                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })

        self._OnIHavePlannedPeriodsLoaded = OnLoadPromise
        return OnLoadPromise
    }

    get PlannedPeriodBounds(): IDateRange {
        return { StartDt: this.WB_Plan.WB_PeriodRow.PeriodStartDt, EndDt: this.WB_Plan.WB_PeriodRow.PeriodEndDt }
    }

    get PlannedPeriods(): WB_PeriodRow[] {
        return this._PlannedPeriods
    }

    get AllPlannedPeriods(): WB_PeriodRow[] {
        return this._PlannedPeriods
    }

    //</editor-fold >

    //<editor-fold desc = "ICubeSource PlanMetric operations">
    private _bICubeSourcePlanMetricsLoaded: boolean
    private _bICubeSourcePlanMetricsEnabled: boolean
    private _cubeSourcePlanMetrics: CubeSource
    private _qsCubeSourcePlanMetrics_sync: ExecutionSerializer
    private _subCubeSourcePrdGrpChange: Subscription = undefined

    private ICubeSourcePlanMetrics_init() {
        this._bICubeSourcePlanMetricsLoaded = false
        this._bICubeSourcePlanMetricsEnabled = false
        this._cubeSourcePlanMetrics = undefined
        this._qsCubeSourcePlanMetrics_sync = new ExecutionSerializer(
            this,
            this._CubeSourcePlanMetrics_sync.bind(this),
            [{}],
            250,
            false
        )
    }

    ICubeSourcePlanMetrics_beforeReload() {
        if (!this._bICubeSourcePlanMetricsEnabled) return

        this._bICubeSourcePlanMetricsLoaded = false

        // Reset the CubeSource Queue

        if (this._subCubeSourcePrdGrpChange) {
            this._subCubeSourcePrdGrpChange.unsubscribe()
        }
    }

    private async ICubeSourcePlanMetrics_OnLoadComplete(): Promise<void> {
        const self = this

        if (self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd)
            self._subCubeSourcePrdGrpChange = self.ProductGroupsChanged.subscribe((iHavePGs) => {
                if (_.isEmpty(iHavePGs)) return

                if (self.bIsLoaded) {
                    self.CubeSourcePlanMetrics_sync()
                }
            })
    }

    get bICubeSourcePlanMetricsLoaded(): boolean {
        return this._bICubeSourcePlanMetricsLoaded
    }

    get OnICubeSourcePlanMetricsLoaded(): Promise<void> {
        const self = this

        self._bICubeSourcePlanMetricsEnabled = self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd
        if (!self._bICubeSourcePlanMetricsEnabled) {
            self._bICubeSourcePlanMetricsLoaded = true
            return ASync.resolve()
        }

        return (async () => {
            if (!self.bIsLoaded) await self.OnLoadComplete

            let ptr = self.WB_Plan.WB_PlanTypeRow.cif

            await self.OnIHaveProductGroupsLoaded
            await self.OnIHavePlannedPeriodsLoaded
            let arrKnownPrdGrpKeys = _.map(self.ProductGroups, (a) => a.cif.WB_PRDGRP_ID)
            let arrKnownPeriodKeys = _.map(self.PlannedPeriods, (a) => a.cif.WB_PERIOD_ID)

            let bEnableLazyLoading = self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleLazyLoadingInd
            if (bEnableLazyLoading && ptr.HR_LAZYLOAD_PG_THRESHOLD > 0) {
                bEnableLazyLoading = arrKnownPrdGrpKeys.length >= ptr.HR_LAZYLOAD_PG_THRESHOLD
            }

            if (bEnableLazyLoading) self.resetLazingLoadingFilters()

            self._cubeSourcePlanMetrics = new CubeSource({
                WbCubeSourceId: self.WB_Plan.WB_PlanTypeRow.cif.WB_CUBESOURCE_ID,
                Parameters: { Plan: [self.WB_Plan.cif.WB_PLAN_ID] },
                dbContext: self.dbContext,
                DimensionPolicyOptions: {
                    CubeSource: undefined,
                    WB_PLANM_ID: self.WB_Plan.cif.WB_PLANM_ID,
                    WB_PlanTypeRow: self.WB_Plan.WB_PlanTypeRow,
                    EffectiveStartDt: self.WB_Plan.WB_PeriodRow.PeriodStartDt,
                    EffectiveEndDt: self.WB_Plan.WB_PeriodRow.PeriodEndDt
                },
                LoadOptions: {
                    PlanVMT: {
                        [DimensionType.Product]: {
                            bEnableLazyLoading: bEnableLazyLoading
                        }
                    }
                },
                KnownKeyOptions: {
                    PlanVMT: {
                        [DimensionType.Product]: {
                            bUseKnownKeys: true,
                            KnownLeafKeys: arrKnownPrdGrpKeys
                        },
                        [DimensionType.Time]: {
                            bUseKnownKeys: true,
                            KnownLeafKeys: arrKnownPeriodKeys
                        }
                    }
                },
                UOMPolicyOptions: {
                    WB_PLANM_ID: self.WB_Plan.cif.WB_PLANM_ID,
                    WB_PlanTypeRow: self.WB_Plan.WB_PlanTypeRow
                },
                UOMConverterOptions: {
                    WB_PLANM_ID: self.WB_Plan.cif.WB_PLANM_ID,
                    WB_PlanTypeRow: self.WB_Plan.WB_PlanTypeRow
                }
            })

            await self._cubeSourcePlanMetrics.OnICubeSourceLoadComplete

            self._cubeSourcePlanMetrics.setEnabled(self.PolicyCollection.BOis.Editable)

            self.defaultHRViews()

            self._bICubeSourcePlanMetricsLoaded = true

            if (self.ProductGroups.length <= PlanMetrics_ProductGroupLoadAllThreshold) {
                // Load PGs
            }
        })()
    }

    get CubeSourcePlanMetrics(): ICubeSource {
        return this._cubeSourcePlanMetrics
    }

    CubeSource_onPrdGrpAdd(prdGrp: WB_ProductGroupRow): void {
        const self = this
        if (_.isEmpty(prdGrp)) return

        if (!this.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd) return

        // Notify Datasource has loaded PG
        let ds: DataSourceJSData = <DataSourceJSData>self._cubeSourcePlanMetrics.getDataSource("PlanVMT")
        ds.setLoadedKeys(DimensionType.Product, prdGrp.cif.WB_PRDGRP_ID)
    }

    CubeSourcePlanMetrics_sync(): void {
        if (!this._bICubeSourcePlanMetricsEnabled) return

        // Debounce calls ...
        console.log("Synchronizing CubeSource Plan Metrics: qsRun")
        this._qsCubeSourcePlanMetrics_sync.Run({})
    }

    private async _CubeSourcePlanMetrics_sync(opts: any): Promise<void> {
        const self = this

        try {
            if (!self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd) return

            if (!self.bICubeSourcePlanMetricsLoaded) await self.OnICubeSourcePlanMetricsLoaded

            // Sync records to PlanVMT DataSource,
            // Set Known Keys

            let ds: DataSourceJSData = <DataSourceJSData>self._cubeSourcePlanMetrics.getDataSource("PlanVMT")
            let prdLoadScope = ds.getLoadStatus([DimensionType.Product])[DimensionType.Product]

            // Determine the Product Groups & Weeks... and create sync the combination of records..
            // Determine PG Scope
            let arrProductGroups: WB_ProductGroupRow[] = [] // self.ProductGroups
            if (prdLoadScope.bLoadAllLoaded) {
                arrProductGroups = self.ProductGroups
            } else {
                // Use the intersection of the Plan Product Groups and What' loaded (allows for remove pg to work)
                let dctAllProductGroups = _.keyBy(self.ProductGroups, (a) => {
                    return a.cif.WB_PRDGRP_ID
                })
                _.forEach(_.values(prdLoadScope.dctLeafIdLoadedById), (wbPrdGrpId) => {
                    if (wbPrdGrpId in dctAllProductGroups) {
                        arrProductGroups.push(dctAllProductGroups[wbPrdGrpId])
                    }
                })
            }
            let dctProductGroups = _.keyBy(arrProductGroups, (aPG) => aPG.cif.WB_PRDGRP_ID)

            // Determine Period Scope
            let arrPeriods: WB_PeriodRow[] = self.PlannedPeriods
            let dctPeriods: { [id: number]: WB_PeriodRow } = _.keyBy(
                arrPeriods,
                (aPrd: WB_PeriodRow) => aPrd.cif.WB_PERIOD_ID
            )
            let dctPlanVolumeMetrics: { [id: string]: WB_PlanVolumeMetricsRow } = {}

            // Create Dictionary... deleting duplicate rows, and rows not in scope
            let removeKeys: number[] = []
            let addRows: WB_PlanVolumeMetricsRow[] = []

            let loadedRows: WB_PlanVolumeMetricsRow[] = <WB_PlanVolumeMetricsRow[]>ds.LoadedRows
            _.forEach(loadedRows, (aRow) => {
                if (!aRow.isActive) {
                    removeKeys.push(aRow.cif.WB_PLANVMT_ID)
                    return
                }

                // Check if the Product Group or Period isn't in the dataset
                if (!(aRow.cif.WB_PRDGRP_ID in dctProductGroups)) {
                    // Remove extra Product Group
                    aRow.markDeleted()
                    removeKeys.push(aRow.cif.WB_PLANVMT_ID)
                    return
                }

                if (!(aRow.cif.WB_PERIOD_ID in dctPeriods)) {
                    // Remove extra Period
                    aRow.markDeleted()
                    removeKeys.push(aRow.cif.WB_PLANVMT_ID)
                    return
                }

                let logicalKey = aRow.cif.WB_PRDGRP_ID + "-" + aRow.cif.WB_PERIOD_ID

                if (logicalKey in dctPlanVolumeMetrics) {
                    // Remove Duplicate Row
                    aRow.markDeleted()
                    removeKeys.push(aRow.cif.WB_PLANVMT_ID)
                    return
                }

                dctPlanVolumeMetrics[logicalKey] = aRow
            }) // Next Row to check for Delete

            // Create Missing Rows
            // get Period Records from this dbContext..
            for (let idxPG = 0; idxPG < arrProductGroups.length; idxPG++) {
                for (let idxPeriod = 0; idxPeriod < arrPeriods.length; idxPeriod++) {
                    let aProductGroupRow = arrProductGroups[idxPG]
                    let aPeriodRow = arrPeriods[idxPeriod]

                    let logicalKey = aProductGroupRow.cif.WB_PRDGRP_ID + "-" + aPeriodRow.cif.WB_PERIOD_ID
                    if (logicalKey in dctPlanVolumeMetrics) {
                        continue
                    }

                    // Create Missing Record.. add to Dictionary.
                    let newRecord: WB_PlanVolumeMetricsRow = <any>(
                        await self.dbContext.createRecordWithKey(esWB_Plan_Volume_Metrics)
                    )
                    newRecord.setDefaults()
                    newRecord.cif.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                    newRecord.cif.WB_PERIOD_ID = aPeriodRow.cif.WB_PERIOD_ID
                    newRecord.WB_PeriodRow = aPeriodRow
                    newRecord.cif.WB_PRDGRP_ID = aProductGroupRow.cif.WB_PRDGRP_ID
                    newRecord.WB_ProductGroupRow = aProductGroupRow
                    self.dbContext.add(esWB_Plan_Volume_Metrics, newRecord)

                    addRows.push(newRecord)
                } // Next Period
            } // Next PG

            // Remove Old, Add Missing, Set Known Keys
            if (!_.isEmpty(removeKeys)) ds.remove(removeKeys)
            if (!_.isEmpty(addRows)) ds.add(addRows)
            ds.setKnownKeyOptions({
                [DimensionType.Product]: {
                    bUseKnownKeys: true,
                    KnownLeafKeys: _.map(self.ProductGroups, (a) => a.cif.WB_PRDGRP_ID)
                }
            })
        } catch (err) {
            throw err
        } finally {
        }
    }

    private resetLazingLoadingFilters() {
        const self = this

        _.forEach(
            ["BVPPerspectiveOptions", "SVFPerspectiveOptions", "MetricPerspectiveOptions"],
            (perspectiveFieldName) => {
                //--

                let perspOptions: ICubePerspectiveOptions = (<any>self.WB_Plan)[perspectiveFieldName] || {}

                if (!("DimensionFilters" in perspOptions)) perspOptions.DimensionFilters = {}

                perspOptions.DimensionFilters[DimensionType.Product] = { FilterByNodes: [] }

                // @ts-ignore
                self.WB_Plan[perspectiveFieldName] = perspOptions
            }
        )
    }

    private defaultHRViews() {
        const self = this

        let cs = self._cubeSourcePlanMetrics
        let pt = self.WB_Plan.WB_PlanTypeRow
        let dctMtrcViews = _.keyBy(self._cubeSourcePlanMetrics.getMetricViews(), (a) => a.MetricViewId)
        let profiles: WB_CubeSourcePerspectiveRow[] = _.filter(self._cubeSourcePlanMetrics.getProfiles(), p => p.isActive && p.cif.SOURCE_WB_ID == self.WB_Plan.cif.WB_PLANM_ID)
        let BVBProfile = _.first(_.filter(_.orderBy(profiles, "VIEW_ORDER"), p => p.cif.SOURCE_SUBTYPE == "BVB" && p.cif.VIEW_TYPE == "HR"))
        let SVFProfile = _.first(_.filter(_.orderBy(profiles, "VIEW_ORDER"), p => p.cif.SOURCE_SUBTYPE == "SVF" && p.cif.VIEW_TYPE == "HR"))
        let SummaryProfile = _.first(_.filter(_.orderBy(profiles, "VIEW_ORDER"), p => p.cif.SOURCE_SUBTYPE == "Summary" && p.cif.VIEW_TYPE == "HR"))

        let hrViews = [
            {
                pOptsField: "BVPPerspectiveOptions",
                tblOptsField: "BVPTableOptions",
                dfltPerspOpts: _.cloneDeep(BVBProfile?.PerspectiveOptions),
                dfltTblOpts: _.cloneDeep(BVBProfile?.TableOptions),
                dfltView: pt.cif.BVPMETRICVIEW_DEFAULT,
                dfltViewsCSV: pt.cif.BVPMETRICVIEWS
            },
            {
                pOptsField: "SVFPerspectiveOptions",
                tblOptsField: "SVFTableOptions",
                dfltPerspOpts: _.cloneDeep(SVFProfile?.PerspectiveOptions),
                dfltTblOpts: _.cloneDeep(SVFProfile?.TableOptions),
                dfltView: pt.cif.SVFMETRICVIEW_DEFAULT,
                dfltViewsCSV: pt.cif.SVFMETRICVIEWS
            },
            {
                pOptsField: "MetricPerspectiveOptions",
                tblOptsField: "MetricTableOptions",
                dfltPerspOpts: _.cloneDeep(SummaryProfile?.PerspectiveOptions),
                dfltTblOpts: _.cloneDeep(SummaryProfile?.TableOptions),
                dfltView: pt.cif.METRICVIEW_DEFAULT,
                dfltViewsCSV: pt.cif.METRICVIEWS
            }
        ]

        function perspectiveIsEmpty(pOpts: ICubePerspectiveOptions): boolean {
            return _.isEmpty(pOpts) || _.isEmpty(pOpts?.Dimensions)
        }

        function tableOptionsIsEmpty(tOpts: IHRTableModelOptions): boolean {
            return _.isEmpty(tOpts) || _.isEmpty(tOpts?.DimensionOptions)
        }

        _.forEach(hrViews, (hrView) => {
            //--
            let perspOptions: ICubePerspectiveOptions = (<any>self.WB_Plan)[hrView.pOptsField] || {}
            let tblOptions: IHRTableModelOptions = (<any>self.WB_Plan)[hrView.tblOptsField] || {}

            // Check independently - a plan can have valid perspective options (e.g.
            // BVPPerspectiveOptions parsed fine) while its paired table options failed to
            // parse/load (e.g. BVPTableOptions came back empty), or vice versa, since they're
            // stored and parsed as separate columns. Only skip defaulting if both are present.
            if (!perspectiveIsEmpty(perspOptions) && !tableOptionsIsEmpty(tblOptions)) return

            // First default to the Plan Type settings, else, construct default
            if (!perspectiveIsEmpty(hrView.dfltPerspOpts)) {
                perspOptions = hrView.dfltPerspOpts
                tblOptions = hrView.dfltTblOpts
            } else {
                perspOptions.Dimensions = {
                    [DimensionType.Time]: {
                        LevelId: pt.WB_PeriodTypeRow?.cif?.PERIOD_TYPE_ID || ""
                    }
                }
                tblOptions.DimensionOptions = {
                    [DimensionType.Time]: {
                        DimensionType: DimensionType.Time,
                        bShowIds: false,
                        Placement: DimensionPlacement.OnColumns
                    }
                }
                tblOptions.MetricPlacement = DimensionPlacement.OnRows
            }

            if (!_.isEmpty(hrView.dfltView)) {
                // Use the views to determine the default
                perspOptions.MetricIds = []
                let mtrcView: IMetricView
                let arrViewIds = [hrView.dfltView, ...hrView.dfltViewsCSV.split(",")]
                _.forEach(arrViewIds, (aViewId) => {
                    if (aViewId in dctMtrcViews) {
                        mtrcView = dctMtrcViews[aViewId]
                        if (!_.isEmpty(mtrcView?.MetricIds)) return false
                    }
                })
                if (mtrcView) {
                    perspOptions.MetricIds = _.clone(mtrcView.MetricIds)
                    tblOptions.MetricOrder = perspOptions.MetricIds
                }
            }

            if (_.isEmpty(perspOptions.MetricIds)) {
                let firstIds = _.filter(
                    _.map(cs.getMetrics({ bIncludeWriteBack: false }), (a) => a.MetricId),
                    (b, idx) => +idx < 10
                )
                perspOptions.MetricIds = firstIds || ["PlanVMT_GROSS_LIST_PRICE"]
                tblOptions.MetricOrder = perspOptions.MetricIds
            }

            // @ts-ignore
            self.WB_Plan[hrView.pOptsField] = perspOptions
            // @ts-ignore
            self.WB_Plan[hrView.tblOptsField] = tblOptions

        })
    }

    //</editor-fold>

    //<editor-fold desc = "IHavePlanVolumeMetrics operations">

    private _bIHavePlanMetricsLoaded: boolean
    private _bIHavePlanMetricsEnabled: boolean
    private _PlanMetricsProgressProvider: ProgressProvider
    private _PlanVolumeMetricsCollectionChanged: BehaviorSubject<IHavePlanMetrics>
    private _PlanVolumeMetricsPropertyChanged: BehaviorSubject<IHavePlanMetricsChangedEvent>
    private _qsPlanVolumeMetrics_sync: ExecutionSerializer
    private _subPrdGrpChange: Subscription = undefined

    private _dctPrdGrpRowQueuedById: { [id: number]: WB_ProductGroupRow }
    private _dctPrdGrpRowLoadedById: { [id: number]: WB_ProductGroupRow }
    private _bLoadAllPrdGrpsQueued: boolean
    private _bLoadAllPrdGrpsLoaded: boolean

    private _bQueueLoadProcessRunning: boolean
    private _bQueueLoadProcessPaused: boolean
    private _processQueuePromise: Promise<void>
    private _ProductGroupLoadProgressProvider: IProgressProvider

    private IHavePlanVolumeMetrics_init() {
        this._dctPrdGrpRowQueuedById = {}
        this._dctPrdGrpRowLoadedById = {}
        this._bLoadAllPrdGrpsQueued = false
        this._bLoadAllPrdGrpsLoaded = false

        this._bQueueLoadProcessRunning = false
        this._bQueueLoadProcessPaused = false
        this._processQueuePromise = undefined
        this._ProductGroupLoadProgressProvider = new ProgressProvider()

        this._bIHavePlanMetricsLoaded = false
        this._bIHavePlanMetricsEnabled = false

        this._PlanMetricsProgressProvider = new ProgressProvider()
        this._PlanVolumeMetricsCollectionChanged = new BehaviorSubject<IHavePlanMetrics>(this)
        this._PlanVolumeMetricsPropertyChanged = new BehaviorSubject<IHavePlanMetricsChangedEvent>(undefined)
        this._qsPlanVolumeMetrics_sync = new ExecutionSerializer(
            this,
            this._PlanVolumeMetrics_sync.bind(this),
            [{}],
            250,
            false
        )
    }

    private IHavePlanVolumeMetrics_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            if (self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd) {
                resolve()
                return
            }

            // Iterate over records and setup change events..
            _.forEach(
                _.filter(self.WB_Plan.WB_PlanVolumeMetricsRows, (a) => a.isActive),
                (aRow) => {
                    aRow.setupOnChangeEvents([], self.IHavePlanVolumeMetrics_onRecordChange.bind(self), self)
                }
            )

            self._subPrdGrpChange = self.ProductGroupsChanged.subscribe((iHavePGs) => {
                if (_.isEmpty(iHavePGs)) {
                    return
                }

                if (self.bIsLoaded) {
                    self.PlanVolumeMetrics_sync()
                }
            })

            resolve()
        })
    }

    IHavePlanVolumeMetrics_beforeReload() {
        this._bIHavePlanMetricsLoaded = false

        // Reset the Queue
        this._dctPrdGrpRowQueuedById = {}
        this._dctPrdGrpRowLoadedById = {}
        this._bLoadAllPrdGrpsQueued = false
        this._bLoadAllPrdGrpsLoaded = false

        this._bQueueLoadProcessRunning = false
        this._bQueueLoadProcessPaused = false
        this._processQueuePromise = undefined

        if (this._subPrdGrpChange) {
            this._subPrdGrpChange.unsubscribe()
        }
    }

    private IHavePlanVolumeMetrics_onRecordChange(
        record: WB_PlanVolumeMetricsRow,
        propertyName: string,
        propertyValue: any,
        options: IHavePlanMetrics
    ) {
        this.PlanVolumeMetricsPropertyChanged.next({
            IHavePlanVolumeMetrics: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    get bIHavePlanMetricsLoaded(): boolean {
        return this._bIHavePlanMetricsLoaded
    }

    get OnIHavePlanMetricsLoaded(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            // Only
            let loadPromise
            if (self.bIsLoaded) {
                loadPromise = ASync.resolve()
            } else {
                loadPromise = self.OnLoadComplete
            }
            loadPromise
                .then((ignore) => {
                    return self.OnIHaveProductGroupsLoaded
                })
                .then((ignore) => {
                    return self.OnIHavePlannedPeriodsLoaded
                })
                .then((ignore) => {
                    self._bIHavePlanMetricsLoaded = true
                    if (self.ProductGroups.length <= PlanMetrics_ProductGroupLoadAllThreshold) {
                        self.loadAllProductGroups()
                    }
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    get PlanMetricsProgressProvider(): IProgressProvider {
        return this._PlanMetricsProgressProvider
    }

    get PlanVolumeMetrics(): WB_PlanVolumeMetricsRow[] {
        return _.filter(this.WB_Plan.WB_PlanVolumeMetricsRows, (a: WB_PlanVolumeMetricsRow) => a.isActive)
    }

    get PlanVolumeMetricsCollectionChanged(): BehaviorSubject<IHavePlanMetrics> {
        return this._PlanVolumeMetricsCollectionChanged
    }

    get PlanVolumeMetricsPropertyChanged(): BehaviorSubject<IHavePlanMetricsChangedEvent> {
        return this._PlanVolumeMetricsPropertyChanged
    }

    PlanVolumeMetrics_onPrdGrpAdd(prdGrp: WB_ProductGroupRow): void {
        const self = this
        if (_.isEmpty(prdGrp)) return

        if (!(prdGrp.cif.WB_PRDGRP_ID in self._dctPrdGrpRowLoadedById))
            self._dctPrdGrpRowLoadedById[prdGrp.cif.WB_PRDGRP_ID] = prdGrp
    }

    PlanVolumeMetrics_sync(): void {
        //  debounce calls ...
        console.log("Synchronizing Metrics: qsRun")
        this._qsPlanVolumeMetrics_sync.Run({})
    }

    private _PlanVolumeMetrics_sync(opts: any): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            if (self.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd) {
                resolve()
                return
            }

            console.log("Synchronizing Metrics: running")
            self.PlanMetricsProgressProvider.start("Synchronizing Metrics")

            // This API checks itself if it's loaded... and tries to load..
            let loadPromise: Promise<void>
            if (self.bIHavePlanMetricsLoaded) {
                loadPromise = ASync.resolve()
            } else {
                loadPromise = self.OnIHavePlanMetricsLoaded
            }

            loadPromise
                .then((ignore) => {
                    // Determine the Product Groups & Weeks... and create sync the combination of records..
                    let arrProductGroups: WB_ProductGroupRow[] = [] // self.ProductGroups
                    if (self._bLoadAllPrdGrpsLoaded) {
                        arrProductGroups = self.ProductGroups
                    } else {
                        // Use the intersection of the Plan Product Groups and What' loaded (allows for remove pg to work)
                        let dctAllProductGroups = _.keyBy(self.ProductGroups, (a) => {
                            return a.cif.WB_PRDGRP_ID
                        })
                        _.forEach(_.values(self._dctPrdGrpRowLoadedById), (aPG) => {
                            if (aPG.cif.WB_PRDGRP_ID in dctAllProductGroups) {
                                arrProductGroups.push(aPG)
                            }
                        })
                    }
                    let dctProductGroups = _.keyBy(arrProductGroups, (aPG) => aPG.cif.WB_PRDGRP_ID)
                    let arrPeriods: WB_PeriodRow[] = self.PlannedPeriods
                    let dctPeriods: { [id: number]: WB_PeriodRow } = _.keyBy(
                        arrPeriods,
                        (aPrd: WB_PeriodRow) => aPrd.cif.WB_PERIOD_ID
                    )
                    let dctPlanVolumeMetrics: { [id: string]: WB_PlanVolumeMetricsRow } = {}

                    // Create Dictionary... deleting duplicate rows, and rows not in scope
                    _.forEach(self.PlanVolumeMetrics, (aRow) => {
                        // Check if the Product Group or Period isn't in the dataset
                        if (!(aRow.cif.WB_PRDGRP_ID in dctProductGroups)) {
                            // Remove extra Product Group
                            aRow.markDeleted()
                            return
                        }

                        if (!(aRow.cif.WB_PERIOD_ID in dctPeriods)) {
                            // Remove extra Period
                            aRow.markDeleted()
                            return
                        }

                        let logicalKey = aRow.cif.WB_PRDGRP_ID + "-" + aRow.cif.WB_PERIOD_ID

                        if (logicalKey in dctPlanVolumeMetrics) {
                            // Remove Duplicate Row
                            aRow.markDeleted()
                            return
                        }

                        dctPlanVolumeMetrics[logicalKey] = aRow
                    })

                    async function createMissingRecords() {
                        // get Period Records from this dbContext..

                        for (let idxPG = 0; idxPG < arrProductGroups.length; idxPG++) {
                            for (let idxPeriod = 0; idxPeriod < arrPeriods.length; idxPeriod++) {
                                let aProductGroupRow = arrProductGroups[idxPG]
                                let aPeriodRow = arrPeriods[idxPeriod]

                                let logicalKey = aProductGroupRow.cif.WB_PRDGRP_ID + "-" + aPeriodRow.cif.WB_PERIOD_ID
                                if (logicalKey in dctPlanVolumeMetrics) {
                                    continue
                                }

                                // Create Missing Record.. add to Dictionary.
                                let newRecord: WB_PlanVolumeMetricsRow = <any>(
                                    await self.dbContext.createRecordWithKey(esWB_Plan_Volume_Metrics)
                                )
                                newRecord.setDefaults()
                                newRecord.cif.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                                newRecord.cif.WB_PERIOD_ID = aPeriodRow.cif.WB_PERIOD_ID
                                newRecord.WB_PeriodRow = aPeriodRow
                                newRecord.cif.WB_PRDGRP_ID = aProductGroupRow.cif.WB_PRDGRP_ID
                                newRecord.WB_ProductGroupRow = aProductGroupRow
                                self.dbContext.add(esWB_Plan_Volume_Metrics, newRecord)

                                // Add Event handling
                                newRecord.setupOnChangeEvents(
                                    [],
                                    self.IHavePlanVolumeMetrics_onRecordChange.bind(self),
                                    self
                                )
                            }
                        }
                    }

                    return createMissingRecords()
                })
                .then((ignore) => {
                    self.PlanMetricsProgressProvider.finish()
                    self.PlanVolumeMetricsCollectionChanged.next(self)
                    resolve()
                })
                .catch((err) => {
                    self.PlanMetricsProgressProvider.exception(err)
                    self.PlanVolumeMetricsCollectionChanged.next(self)
                    error(err)
                })
        })
    }

    // Metric Row Load Operations
    pauseLoadProcessing(): void {
        this._bQueueLoadProcessPaused = true
    }

    resumeLoadProcessing(): void {
        this._bQueueLoadProcessPaused = false
        this.IHavePlanVolumeMetrics_processQueue()
    }

    // Load by Product Groups
    loadProductGroups(arrWBPGIds: number[]): void {
        let self = this

        let loadPromise = undefined
        if (_.isEmpty(arrWBPGIds)) {
            loadPromise = ASync.resolve([])
        } else if (_.isArray(arrWBPGIds)) {
            loadPromise = self.dbContext.findUsingCache(esWB_Product_Group, arrWBPGIds)
        } else {
            // Argument not array or empty
            return
        }

        loadPromise
            .then((arrWB_PrdGrp_Rows: WB_ProductGroupRow[]) => {
                if (_.isEmpty(arrWB_PrdGrp_Rows)) {
                    return
                }

                _.forEach(arrWB_PrdGrp_Rows, (aWBPrdGrpRow: WB_ProductGroupRow) => {
                    if (aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowQueuedById) {
                        // Don't re-add it.
                    } else {
                        self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
                    }
                })
                //self._notifyQueueChange()
                self.IHavePlanVolumeMetrics_processQueue()
            })
            .catch((err) => {
                console.log(err)
                return
            })
    }

    loadAllProductGroups(): void {
        this._bLoadAllPrdGrpsQueued = true
        this.IHavePlanVolumeMetrics_processQueue()
    }

    get ProductGroupsLoaded(): WB_ProductGroupRow[] {
        return _.values(this._dctPrdGrpRowLoadedById)
    }

    get ProductGroupsAllLoaded(): boolean {
        return this._bLoadAllPrdGrpsLoaded
    }

    get ProductGroupsLoadProgressProvider(): IProgressProvider {
        return this._ProductGroupLoadProgressProvider
    }

    public IHavePlanVolumeMetrics_processQueue(): Promise<void> {
        let self = this

        if (this.WB_Plan.WB_PlanTypeRow.bUseHyperRectangleInd) return

        let bDebugQueueProcessing = true

        // 0. If Loading paused, exit
        // 1. If Load Running, exit
        // 2. Copy the queue
        // 3. Check if the queue is empty
        // 4. Load Volume Metrics
        // 5. Delete loaded items from the queue
        // 6. Notify addition

        if (bDebugQueueProcessing) console.log("IHavePlanVolumeMetrics_processQueue started")

        // 0. If Loading Paused, exit
        if (self._bQueueLoadProcessPaused) {
            console.log("IHavePlanVolumeMetricss_processQueue ended: paused")
            return self._processQueuePromise
        }

        // 1. If Load Running, exit
        if (self._bQueueLoadProcessRunning) {
            console.log("IHavePlanVolumeMetrics_processQueue ended: already running")
            return self._processQueuePromise
        }

        self._bQueueLoadProcessRunning = true
        let returnValue = (self._processQueuePromise = (async () => {
            // 2. Copy the queue
            let dctPrdGrpRowByIdCurrentQueueLoad = _.clone(self._dctPrdGrpRowQueuedById)
            let bLoadAllProductGroupCurrentQueueLoad = self._bLoadAllPrdGrpsQueued || self._bLoadAllPrdGrpsLoaded

            // Remove already loaded Items
            _.forEach(self._dctPrdGrpRowLoadedById, (value, key: any) => {
                if (key in dctPrdGrpRowByIdCurrentQueueLoad) {
                    delete dctPrdGrpRowByIdCurrentQueueLoad[key]
                }
            })

            // 3. Check if the queue is empty
            if (
                _.isEmpty(dctPrdGrpRowByIdCurrentQueueLoad) &&
                bLoadAllProductGroupCurrentQueueLoad == self._bLoadAllPrdGrpsLoaded
            ) {
                self._dctPrdGrpRowQueuedById = {}
                console.log("IHavePlanVolumeMetrics_processQueue ended: empty queue")
                self._bQueueLoadProcessRunning = false
                return
            }

            // If All Loaded, return...
            if (self._bLoadAllPrdGrpsLoaded) {
                console.log("IHavePlanVolumeMetrics_processQueue ended: all pgs already loaded")
                self._bQueueLoadProcessRunning = false
                return
            }

            // Mark running
            try {
                self._bQueueLoadProcessRunning = true
                if (bDebugQueueProcessing) console.log("IHavePlanVolumeMetrics_processQueue set Queue Running.")
                self._ProductGroupLoadProgressProvider.start("Loading Metrics")

                let arrWBPrdGrpIds = _.map(dctPrdGrpRowByIdCurrentQueueLoad, (aPrdGrpRow: WB_ProductGroupRow) => {
                    return aPrdGrpRow.cif.WB_PRDGRP_ID
                })

                // 4. Union of all queued & loaded
                let condition: any = { WB_PLAN_ID: self.WB_Plan.cif.WB_PLAN_ID }

                if (!bLoadAllProductGroupCurrentQueueLoad) {
                    if (_.isEmpty(arrWBPrdGrpIds)) {
                        arrWBPrdGrpIds = [-99999]
                    }

                    condition = { and: [condition, { WB_PRDGRP_ID: { inq: arrWBPrdGrpIds } }] }
                }
                if (bDebugQueueProcessing) console.log("Queue Condition: " + JSON.stringify(condition))

                //self._ProductGroupLoadProgressProvider.setProgressDenominator(arrWBEventToLoad.length)
                // Use Record Resolver to get Parents..
                let recResolver = new ESRecordResolver(self.dbContext, {})
                let results = await recResolver.queryAllWithRelationships(esWB_Plan_Volume_Metrics, condition)

                if (!_.isArray(results)) {
                    if (bDebugQueueProcessing)
                        console.log("IHavePlanVolumeMetrics_processQueue set Queue Running = false, not array.")
                    self._bQueueLoadProcessRunning = false
                    self._ProductGroupLoadProgressProvider.finish()
                    return
                }

                // Add Event handling
                _.forEach(results, (loadRecord) => {
                    loadRecord.setupOnChangeEvents([], self.IHavePlanVolumeMetrics_onRecordChange.bind(self), self)
                })

                // 5. Delete loaded items from the queue, Update Loaded List
                // dctPrdGrpRowByIdCurrentQueueLoad
                // bLoadAllProductGroupCurrentQueueLoad

                _.forEach(dctPrdGrpRowByIdCurrentQueueLoad, (aWBPrdGrpRow: WB_ProductGroupRow) => {
                    // Remove PrdGrps from Queue
                    if (aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowQueuedById) {
                        delete self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID]
                    }

                    // Add to Loaded
                    if (!(aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowLoadedById)) {
                        self._dctPrdGrpRowLoadedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
                    }
                })

                self._bLoadAllPrdGrpsLoaded = bLoadAllProductGroupCurrentQueueLoad

                if (bDebugQueueProcessing)
                    console.log("IHavePlanVolumeMetrics_processQueue set Queue Running = false, done.")
                self._bQueueLoadProcessRunning = false

                self._ProductGroupLoadProgressProvider.finish()

                //self._notifyQueueChange()

                self.PlanVolumeMetrics_sync()

                setTimeout(function() {
                    self.IHavePlanVolumeMetrics_processQueue()
                }, 0)
            } catch (err) {
                console.log("IHavePlanVolumeMetrics_processQueue set Queue Running = false, error.")
                self._bQueueLoadProcessRunning = false
                self._ProductGroupLoadProgressProvider.exception(err)
            }
        })())

        return returnValue
    }

    //
    //</editor-fold>

    //<editor-fold desc = "IHaveDisplays" >
    private _bIHaveDisplaysLoaded: boolean
    private _PlanDisplaysProgressProvider: ProgressProvider
    private _PlanDisplaysCollectionChanged: BehaviorSubject<IHaveDisplays>
    private _PlanDisplaysPropertyChanged: BehaviorSubject<IHaveDisplaysChangedEvent>

    private IHaveDisplays_init() {
        this._bIHaveDisplaysLoaded = false

        this._PlanDisplaysProgressProvider = new ProgressProvider()
        this._PlanDisplaysCollectionChanged = new BehaviorSubject<IHaveDisplays>(this)
        this._PlanDisplaysPropertyChanged = new BehaviorSubject<IHaveDisplaysChangedEvent>(undefined)
    }

    private IHaveDisplays_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            // Iterate over records and setup change events..
            _.forEach(
                _.filter(self.WB_Plan.WB_PlanProductMetricsRows, (a) => a.isActive),
                (aRow) => {
                    aRow.setupOnChangeEvents([], self.IHaveDisplays_onRecordChange.bind(self), self)
                }
            )

            resolve()
        })
    }

    private IHaveDisplays_onRecordChange(
        record: WB_PlanProductMetricsRow,
        propertyName: string,
        propertyValue: any,
        options: IHaveDisplays
    ) {
        this.PlanDisplaysPropertyChanged.next({
            IHaveDisplays: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    IHaveDisplays_beforeReload() {
        this._bIHaveDisplaysLoaded = false
        this.dbContext.purgeCache(esWB_Plan_Product_Metrics, this.PlanDisplayMetrics, {})
    }

    get bIHaveDisplaysLoaded(): boolean {
        return this._bIHaveDisplaysLoaded
    }

    get OnIHaveDisplaysLoaded(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            // Only
            let loadPromise
            if (self.bIsLoaded) {
                loadPromise = ASync.resolve()
            } else {
                loadPromise = self.OnLoadComplete
            }
            loadPromise
                .then((ignore) => {
                    return self.OnIHaveProductGroupsLoaded
                })
                .then((ignore) => {
                    return self.OnIHavePlannedPeriodsLoaded
                })
                .then((ignore) => {
                    self._bIHaveDisplaysLoaded = true
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    get PlanDisplaysProgressProvider(): IProgressProvider {
        return this._PlanDisplaysProgressProvider
    }

    get PlanDisplayMetrics(): WB_PlanProductMetricsRow[] {
        return _.filter(this.WB_Plan.WB_PlanProductMetricsRows, (a: WB_PlanProductMetricsRow) => a.isActive)
    }

    get PlanDisplaysCollectionChanged(): BehaviorSubject<IHaveDisplays> {
        return this._PlanDisplaysCollectionChanged
    }

    get PlanDisplaysPropertyChanged(): BehaviorSubject<IHaveDisplaysChangedEvent> {
        return this._PlanDisplaysPropertyChanged
    }

    //</editor-fold>

    //<editor-fold desc = "IHavePlanCalculator operations">

    private _PlanCalculator: TPMPlanCalculator

    private IHavePlanCalculator_init() {
        this._PlanCalculator = new TPMPlanCalculator()
        this._PlanCalculator.Register(this)
    }

    get PlanCalculator(): IPlanCalculator {
        return this._PlanCalculator
    }

    //</editor-fold>

    //<editor-fold desc = "IHavePlanStandardCalculator operations">

    private _PlanStandardCalculator: TPMPlanStandardCalculator

    private IHavePlanStandardCalculator_init() {
        this._PlanStandardCalculator = new TPMPlanStandardCalculator()
    }

    private IHavePlanStandardCalculator_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self._PlanStandardCalculator.Register(self)
            resolve()
        })
    }

    get PlanStandardCalculator(): IPlanStandardCalculator {
        return this._PlanStandardCalculator
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveSecurity operations">

    private _PolicyCollection: IPolicyCollection
    private _bIHaveSecurityLoaded: boolean
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>
    private _Lock: ILock

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection()
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
        this._Lock = undefined
    }

    IHaveSecurity_beforeReload() {
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded.then((ignore) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this

        if (this.bIHaveSecurityLoaded) {
            return ASync.resolve(this)
        }

        if (this._OnIHaveSecurityLoaded) {
            return this._OnIHaveSecurityLoaded
        }

        let aHasRolePolicy: PolicyHasRole
        this._OnIHaveSecurityLoaded = new Promise<IHaveSecurity>((resolve, error) => {
            let aLock: ILock = undefined
            if (_.isEmpty(self._Lock)) {
                let aLockService = new PALockClient()
                aLock = aLockService.createLock("TPMPlan", this.drWB_Plan.cif.WB_PLAN_ID, {
                    object: {
                        UI_Id: self.drWB_Plan.cif.PLAN_ID,
                        Name: self.drWB_Plan.cif.PLAN_NAME
                    }
                })
            } else aLock = self._Lock

            aLock
                .OnLoaded()
                .then((ignored: any) => {
                    return aLock.acquire()
                })
                .then((ignored: any) => {
                    self._Lock = aLock
                    let aBOis = new PolicyBOis(
                        ["Super_User", "TPM_SVF_Editor"],
                        [],
                        self.WB_Plan.cif.WB_CUST_ID_PLANNING,
                        aLock,
                        self.WB_Plan.IsClosed
                    )
                    self._PolicyCollection.addPolicy("BOis", aBOis)
                    aHasRolePolicy = new PolicyHasRole(self.WB_Plan.cif.WB_CUST_ID_PLANNING)
                    return aHasRolePolicy.OnLoadComplete
                })
                .then((ignored: any) => {
                    self._PolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                    this._bIHaveSecurityLoaded = true
                    resolve()
                })
                .catch((err: any) => {
                    error(err)
                })
        })

        return this._OnIHaveSecurityLoaded
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded
    }

    //</editor-fold>

    //<editor-fold desc = "Audit Operations">
    get BaselineAcceptedChanged(): BehaviorSubject<IBusinessObject> {
        return this._BaselineAcceptedChanged
    }

    async addBaselineAcceptedAuditItem(totalUnits: string): Promise<void> {
        let self = this

        let auditService = await TPMPlanBOAudit.AcceptAdjBaseTPMPlanAuditBO(self, totalUnits)
        self.dctBaselineAcceptedAuditService.push(auditService)

        return
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveEditorLock
    get EditorLock(): ILock {
        return this._Lock
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveRefCounter
    private _refCounter: IRefCounter

    RefCounter_Init(): void {
        this._refCounter = new RefCounter(this)
        this.RefCounter.OnRefCounterChanged.subscribe(this.onRefCounterChanged.bind(this))
    }

    get RefCounter(): IRefCounter {
        return this._refCounter
    }

    private onRefCounterChanged(aRefCounter: IRefCounter): void {
        if (this.RefCounter.RefCount > 0) return

        if (_.isEmpty(this.EditorLock)) return

        if (this.EditorLock.Status === "Locked") this.EditorLock.release()
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolverProvider   <<STATIC>> operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ("dbContext" in opts) dbContext = opts.dbContext
        else {
            dbContext = new PAdbContext({})
        }
        const pln_options: CreateOptions = {
            standardConditions: {
                [esWB_Plan]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Product_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Plan_Product]: { DELETED_IND: { notIn: ["1"] } }
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_Plan]: [esWB_Plan_Volume_Metrics, esWB_Plan_Redistrib_Volume_Metrics, esWB_Plan_Funding_Metrics]
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Plan_Type]: [esWB_Period_Type]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, pln_options)

        return aRecResolver
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Plan, ids)
                .then((result) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 5
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(TPMPlanBO, "TPMPlanBO", "TPM Plan")
